import React from 'react';
import { useLoginRedirect } from '@hooks/useLoginRedirect';
import { CampaignBanner } from '@naf-simba/sections-campaign-banner';
import BlockContent from '@components/block-content/BlockContent';
import { useResponsiveImageSize } from '@hooks/useResponsiveImageSize';
import { useDocumentUrlWithNode } from '@hooks/Documents/useDocumentUrl';
import { Campaign } from '@appTypes/benefitsType';
import { useCloudinary } from '@hooks/Cloudinary/useCloudinary';
import { useTrackCTA } from '@utils/Tracking/CtaClick/TrackCtaClick';
import { CampaignDiscount } from './CampaignDiscount/CampaignDiscount';
import { createHandleLoginClick } from './utils';
import * as S from './CampaignBannerMapper.styles';

type Props = {
  campaigns: Campaign[];
  handleCtaBlankExternalTracking: any;
};

type CampaignBannerItemProps = {
  camp: Campaign;
  campaignsLength: number;
  handleCtaBlankExternalTracking: Props['handleCtaBlankExternalTracking'];
  handleLoginClick: () => void;
  imageWidth?: number;
  getResponsiveImage: ReturnType<typeof useCloudinary>['getResponsiveImage'];
};

const CampaignBannerItem = ({
  camp,
  campaignsLength,
  handleCtaBlankExternalTracking,
  handleLoginClick,
  imageWidth,
  getResponsiveImage,
}: CampaignBannerItemProps) => {
  const { accessDetails, image, label, name, color, campaignCallToAction, mainText, type } = camp;
  const accessAction = accessDetails?.accessAction;

  const campImage = getResponsiveImage(image?.publicId, {
    width: imageWidth,
    focusMethod: image?.focus,
  })?.toURL();

  const ctaInternalUrl = useDocumentUrlWithNode(campaignCallToAction?.ctaInternalLink?.referenceComponent);
  const ctaUrl =
    campaignCallToAction?.url ||
    (ctaInternalUrl && campaignCallToAction?.ctaInternalLink?.anchorId
      ? `${ctaInternalUrl}#${campaignCallToAction.ctaInternalLink.anchorId}`
      : ctaInternalUrl);
  const hasValidCta = !!ctaUrl && !!campaignCallToAction?.buttonText;
  const ctaForTracking = campaignCallToAction && ctaUrl ? { ...campaignCallToAction, url: ctaUrl } : undefined;

  const showCTA = (accessAction === 'None' || type === 'tips') && hasValidCta;
  const showAccessUI = !!accessAction;

  return (
    <S.BannerWrapper $restrainWidth={campaignsLength > 2}>
      <CampaignBanner
        label={label}
        title={name}
        color={color === 'yellow20' ? 'primary' : 'secondary'}
        media={campImage ? { src: campImage, alt: image?.altText || '' } : undefined}
        size="sm"
        description={<BlockContent value={mainText} />}
        descriptionAs="div"
        primaryAction={
          showCTA && campaignCallToAction
            ? {
                label: campaignCallToAction.buttonText,
                href: ctaUrl,
                ...(campaignCallToAction.blank ? { target: '_blank' as const, rel: 'noopener noreferrer' } : {}),
                onClick: handleCtaBlankExternalTracking(ctaForTracking),
              }
            : undefined
        }
        extra={showAccessUI ? <CampaignDiscount campaign={camp} onLoginClick={handleLoginClick} /> : undefined}
      />
    </S.BannerWrapper>
  );
};

export const CampaignBannerMapper = ({ campaigns, handleCtaBlankExternalTracking }: Props) => {
  const { getResponsiveImage } = useCloudinary();
  const { redirectToLoginPage } = useLoginRedirect();
  const responsiveImagesSize = useResponsiveImageSize(675);
  const trackCTA = useTrackCTA();

  const handleLoginClick = createHandleLoginClick(trackCTA, redirectToLoginPage);

  return campaigns.map((camp) => (
    <CampaignBannerItem
      key={camp.id}
      camp={camp}
      campaignsLength={campaigns.length}
      handleCtaBlankExternalTracking={handleCtaBlankExternalTracking}
      handleLoginClick={handleLoginClick}
      imageWidth={responsiveImagesSize?.width}
      getResponsiveImage={getResponsiveImage}
    />
  ));
};

export default CampaignBannerMapper;
